import classNames from "classnames";
import React from "react";
import { connect } from "react-redux";
import _ from "underscore";
import { Loader } from "../../../core/atoms/atoms";
import { ActionPromises } from "../../../web/state/actionPromises";
import { CollectionKeyHelper } from "../../helpers/contentCollectionHelpers";
import { ContentTypeEnumHelper } from "@travellocal/utils/enumHelper";
import { MatchState } from "../../helpers/reduxHelpers";
import { PlaceholderCard } from "./cards";
import { ContentCard } from "@travellocal/ui";

import "./cardContainer.scss";

export interface ICardContainerProps {
  /**
   * Key used to shard content pages in the redux store.
   * Each separate place CardContainer is used should use a different collectionKey.
   */
  collectionKey: string;

  /**
   * Parameters to pass to the Inspiration API when fetching content for this CardContainer.
   */
  filters?: IListContentParams;

  /**
   * The maximum number of cards to show.
   */
  cardLimit?: number;

  /**
   * If set, the card for this piece of content will not be shown if it is returned in the results from the Inspiration API.
   */
  seedItem?: IContent;

  /**
   * If set, the hero version of the content card will be shown for the first item.
   */
  hasHeroCard?: boolean | ((cardCount: number) => boolean);

  /**
   * Classname for the .card-container wrapper.
   */
  className?: string;

  /**
   * Classname for the .content-cards within.
   */
  cardClassName?: string;

  /**
   * If defined, makes the card container use Bulma columns.
   * Wraps each card and child element in a column with the provided class.
   */
  columnClassName?: string;

  renderCardWrapper?: (elements: React.ReactNode) => React.ReactNode;

  /**
   * If set to false, nested `.column` divs sit on one line
   */
  isMultineColumn?: boolean;

  /**
   * Number alters focus state of links within content cards, and if/where it participates in sequential keyboard navigation.
   */
  tabIndex?: number;

  onClick?: () => void;
}

interface DispatchProps {
  dispatch: any;
  loadContent: () => Promise<any>;
}

interface StoreProps {
  currencyState: IState<IBaseCurrency>;
  contentState: IKeyedState<IPagerState<IContent>>;
}

type Props = ICardContainerProps & DispatchProps & StoreProps;

const storeToProps = (store: IWebStoreState): StoreProps => ({
  currencyState: store.currency,
  contentState: store.contentByCollection,
});

const dispatchToProps = (dispatch, ownProps: ICardContainerProps): DispatchProps => {
  return {
    dispatch,
    loadContent: () => {
      const context = { key: ownProps.collectionKey };
      return dispatch(ActionPromises.getContentPage(ownProps.filters, -1, ownProps.cardLimit + 1, context));
    },
  };
};

/**
 * Container for inspiration cards that refreshes whenever the site currency changes.
 * Does not support paging.
 */
class CardContainerComponent extends React.Component<Props> {
  public static defaultProps = {
    isMultineColumn: true,
  };

  public componentWillReceiveProps(newProps: Props) {
    if (!_.isEqual(this.props.currencyState.data, newProps.currencyState.data)) {
      // We don't need to reload unless our filters could allow sample itineraries
      if (!newProps.filters.types || newProps.filters.types.some(x => x === ContentType.SampleItinerary)) {
        this.props.loadContent();
      }
    }
  }

  public componentWillMount() {
    // Reload the content -  If currency state changes whilst the inspiration dropdown is not active
    if (this.props.collectionKey === CollectionKeyHelper.DropDownContentItineraries) {
      const collectionState = this.props.contentState.data[this.props.collectionKey];
      const content = collectionState && collectionState.data && collectionState.data[0];
      if (content != null && content.type === ContentType.SampleItinerary) {
        const { pricePerPerson } = content.content;
        const currencyId = pricePerPerson && pricePerPerson.currency ? pricePerPerson.currency.id : null;
        if (currencyId !== this.props.currencyState.data.id) {
          this.props.loadContent();
        }
      }
    }
  }

  private renderCards = () => {
    const { contentState, collectionKey, cardLimit } = this.props;
    const collectionState = contentState.data[collectionKey];

    return MatchState(collectionState, {
      pending: () => {
        // If we can guess at how many placeholders to show, try it. Otherwise just show a loader
        const cardCount = cardLimit || (collectionState.data ? collectionState.data.length : 0);
        if (cardCount < 1) {
          return <Loader />;
        }

        const placeholders = _.range(0, cardCount).map(index => (
          <PlaceholderCard
            key={index}
          />
        ));
        return placeholders;
      },
      error: () => <p>Sorry, something went wrong and we couldn't load this.</p>,
      ok: () => {
        const { filters, seedItem, cardClassName, columnClassName, hasHeroCard, onClick, tabIndex } = this.props;

        const data = seedItem && seedItem.id
          ? collectionState.data.filter(content => content.id !== seedItem.id)
          : collectionState.data;

        const items = cardLimit === undefined || cardLimit <= 0
          ? data
          : data.slice(0, cardLimit);

        const cards: React.ReactNode[] = items.map((content, i) => {
          const isHero = i === 0
            && (typeof hasHeroCard === "function" ? hasHeroCard(items.length) : !!hasHeroCard);

          const card = (
            <ContentCard
              isHero={isHero}
              key={ContentTypeEnumHelper.toKey(content)}
              content={content}
              filterContext={filters}
              className={cardClassName}
              tabIndex={tabIndex}
              onClick={onClick}
            />
          );

          if (columnClassName) {
            return (
              <div key={i} className={isHero ? "column is-full" : columnClassName}>
                {card}
              </div>
            );
          }
          else {
            return card;
          }
        });

        if (columnClassName && this.props.children) {
          const wrapped = React.Children.map(this.props.children, (child, i) => (
            <div className={columnClassName} key={i}>
              {child}
            </div>
          ));
          cards.push(...wrapped);
        }
        else {
          cards.push(...React.Children.toArray(this.props.children));
        }

        return cards;
      },
    });
  }

  public render() {
    const { className, columnClassName, renderCardWrapper, isMultineColumn } = this.props;
    const containerClass = classNames("card-container", className, {
      "columns": !!columnClassName,
      "is-multiline": isMultineColumn,
    });

    return (
      <div className={containerClass}>
        {renderCardWrapper ? renderCardWrapper(this.renderCards()) : this.renderCards()}
      </div>
    );
  }
}
export const CardContainer = connect<StoreProps, DispatchProps, ICardContainerProps>(storeToProps, dispatchToProps)(CardContainerComponent);
